import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineSparkles, HiOutlineStatusOnline, HiOutlinePhotograph,
  HiOutlineEye, HiOutlineClock, HiOutlineChat, HiOutlineRefresh,
  HiHeart, HiOutlineHeart, HiOutlineDotsHorizontal,
  HiOutlinePencil, HiOutlineTrash,
} from 'react-icons/hi';
import { feedAPI, profileAPI, creatorAPI, conversationAPI } from '../services/api';
import LoadingAnimation from '../components/LoadingAnimation';
import VerifiedBadge from '../components/VerifiedBadge';
import PostComposer from '../components/PostComposer';
import { getFriendlyError } from '../utils/errorMessage';
import './Feed.css';

const TYPE_META = {
  PROFILE_VIEW: { icon: HiOutlineEye, cls: 'view' },
  ONLINE_NOW: { icon: HiOutlineStatusOnline, cls: 'online' },
  NEW_MEMBER: { icon: HiOutlineSparkles, cls: 'new' },
  NEW_PHOTO: { icon: HiOutlinePhotograph, cls: 'photo' },
  ACTIVE: { icon: HiOutlineClock, cls: 'active' },
};

const thumb = (url) =>
  url ? url.replace('/upload/', '/upload/w_400,h_400,c_fill,q_auto,f_auto/') : null;
const postImg = (url) =>
  url ? url.replace('/upload/', '/upload/w_900,q_auto,f_auto/') : null;

const MOOD_LABELS = {
  CHAT: 'Up for a chat',
  HOOKUP: 'Looking to hook up',
  FWB: 'Open to FWB',
  VIBING: 'Just vibing',
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const iso = ts.endsWith('Z') ? ts : ts + 'Z';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
};

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [items, setItems] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);   // which post's menu is open
  const [editingId, setEditingId] = useState(null);     // which post is being edited
  const [editText, setEditText] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setError('');
      profileAPI.updateActivity().catch(() => {});
      const [postsRes, feedRes, profileRes] = await Promise.all([
        feedAPI.getPosts(0, 20).catch(() => ({ data: [] })),
        feedAPI.getFeed(0, 20).catch(() => ({ data: [] })),
        profileAPI.getMyProfile().catch(() => ({ data: null })),
      ]);
      setPosts(Array.isArray(postsRes.data) ? postsRes.data : []);
      setItems(Array.isArray(feedRes.data) ? feedRes.data : []);
      setProfile(profileRes.data);
    } catch (err) {
      setError(getFriendlyError(err, 'Could not load your feed. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openProfile = (targetUserId) => targetUserId && navigate(`/profile/${targetUserId}`);

  const sayHi = async (e, targetUserId) => {
    e.stopPropagation();
    if (!targetUserId) return;
    try {
      // Chat expects a conversation id, not a user id. Get/create the
      // conversation first, then open it.
      const res = await conversationAPI.getOrCreateConversation(targetUserId);
      const convId = res.data?.id;
      navigate(convId ? `/chat/${convId}` : `/profile/${targetUserId}`);
    } catch (err) {
      // Fall back to the profile if we couldn't open a conversation.
      navigate(`/profile/${targetUserId}`);
    }
  };

  const toggleLike = async (postId) => {
    // Optimistic update
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      const liked = !p.likedByMe;
      return { ...p, likedByMe: liked, likesCount: Math.max(0, (p.likesCount || 0) + (liked ? 1 : -1)) };
    }));
    try {
      await creatorAPI.toggleLike(postId);
    } catch (err) {
      // Revert on failure
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        const liked = !p.likedByMe;
        return { ...p, likedByMe: liked, likesCount: Math.max(0, (p.likesCount || 0) + (liked ? 1 : -1)) };
      }));
    }
  };

  const startEdit = (post) => {
    setMenuOpenId(null);
    setEditingId(post.id);
    setEditText(post.caption || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const saveEdit = async (postId) => {
    const caption = editText.trim();
    // Optimistic update
    setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, caption } : p)));
    setEditingId(null);
    try {
      await creatorAPI.updateContent(postId, caption);
    } catch (err) {
      load(); // reload to restore true state on failure
    }
  };

  const deletePost = async (postId) => {
    setMenuOpenId(null);
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    const prev = posts;
    setPosts((cur) => cur.filter((p) => p.id !== postId)); // optimistic remove
    try {
      await creatorAPI.deleteContent(postId);
    } catch (err) {
      setPosts(prev); // restore on failure
    }
  };

  const isFemale = profile?.gender === 'FEMALE';
  const myUserId = profile?.userId;

  if (loading) {
    return (
      <div className="feed-container">
        <LoadingAnimation />
      </div>
    );
  }

  const hasContent = posts.length > 0 || items.length > 0;

  return (
    <div className="feed-container">
      <header className="feed-header">
        <div>
          <h1>Feed</h1>
          <p className="feed-subtitle">Posts and activity from people near you</p>
        </div>
        <button className="feed-refresh" onClick={load} aria-label="Refresh feed">
          <HiOutlineRefresh />
        </button>
      </header>

      {/* Women can post */}
      {isFemale && (
        <PostComposer
          onPosted={load}
          authorName={profile?.name}
          authorPhotoUrl={profile?.photoUrls && profile.photoUrls.length > 0 ? thumb(profile.photoUrls[0]) : null}
        />
      )}

      {error && (
        <div className="feed-state" role="alert">
          <div className="feed-state-icon"><HiOutlineRefresh /></div>
          <h3>Couldn't load the feed</h3>
          <p>{error}</p>
          <button className="feed-cta" onClick={load}>Try again</button>
        </div>
      )}

      {/* Posts */}
      {posts.length > 0 && (
        <div className="feed-posts">
          {posts.map((post) => {
            const avatar = post.authorPhotoUrl ? thumb(post.authorPhotoUrl) : null;
            const img = postImg(post.contentUrl);
            const isMine = myUserId && post.authorUserId === myUserId;
            const isEditing = editingId === post.id;
            return (
              <article key={`post-${post.id}`} className="post-card">
                <header className="post-card-head">
                  <div className="post-head-main" onClick={() => openProfile(post.authorUserId)}>
                    <div className="post-avatar">
                      {avatar ? (
                        <img src={avatar} alt={post.authorName || 'Member'} loading="lazy" />
                      ) : (
                        <div className="post-avatar-fallback">
                          {(post.authorName || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="post-author">
                      <span className="post-author-name">
                        {post.authorName || 'Member'}
                        {post.authorVerified && <VerifiedBadge size="small" />}
                      </span>
                      <span className="post-time">{timeAgo(post.createdAt)}</span>
                    </div>
                  </div>

                  {isMine && (
                    <div className="post-menu">
                      <button
                        className="post-menu-btn"
                        aria-label="Post options"
                        onClick={() => setMenuOpenId(menuOpenId === post.id ? null : post.id)}
                      >
                        <HiOutlineDotsHorizontal />
                      </button>
                      {menuOpenId === post.id && (
                        <div className="post-menu-dropdown">
                          <button onClick={() => startEdit(post)}>
                            <HiOutlinePencil aria-hidden="true" /> Edit
                          </button>
                          <button className="post-menu-delete" onClick={() => deletePost(post.id)}>
                            <HiOutlineTrash aria-hidden="true" /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </header>

                {img && (
                  <div className="post-image" onDoubleClick={() => toggleLike(post.id)}>
                    <img src={img} alt={post.caption || 'Post'} loading="lazy" />
                  </div>
                )}

                {isEditing ? (
                  <div className="post-edit">
                    <textarea
                      className="post-edit-input"
                      value={editText}
                      maxLength={1000}
                      autoFocus
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div className="post-edit-actions">
                      <button className="post-edit-cancel" onClick={cancelEdit}>Cancel</button>
                      <button className="post-edit-save" onClick={() => saveEdit(post.id)}>Save</button>
                    </div>
                  </div>
                ) : (
                  post.caption && <p className="post-caption">{post.caption}</p>
                )}

                <div className="post-actions">
                  <button
                    className={`post-action ${post.likedByMe ? 'liked' : ''}`}
                    onClick={() => toggleLike(post.id)}
                    aria-label={post.likedByMe ? 'Unlike' : 'Like'}
                  >
                    {post.likedByMe ? <HiHeart /> : <HiOutlineHeart />}
                    <span>{post.likesCount || 0}</span>
                  </button>
                  <button
                    className="post-action post-action--hi"
                    onClick={(e) => sayHi(e, post.authorUserId)}
                  >
                    <HiOutlineChat />
                    <span>Message</span>
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Activity (people) */}
      {items.length > 0 && (
        <>
          <h2 className="feed-section-title">People active now</h2>
          <div className="feed-list">
            {items.map((item) => {
              const p = item.profile || {};
              const meta = TYPE_META[item.type] || TYPE_META.ACTIVE;
              const Icon = meta.icon;
              const photo = p.photoUrls && p.photoUrls.length > 0 ? thumb(p.photoUrls[0]) : null;
              const genderCls = p.gender === 'FEMALE' ? 'female' : p.gender === 'MALE' ? 'male' : 'other';
              const mood = p.currentMood ? MOOD_LABELS[p.currentMood] : null;

              return (
                <article key={item.id} className="feed-card" onClick={() => openProfile(p.userId)}>
                  <div className="feed-avatar">
                    {photo ? (
                      <img src={photo} alt={p.name || 'Member'} loading="lazy" />
                    ) : (
                      <div className={`feed-avatar-fallback feed-avatar-fallback--${genderCls}`}>
                        {(p.name || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    {p.isOnline && <span className="feed-online-dot" title="Online now" />}
                  </div>
                  <div className="feed-body">
                    <div className="feed-name-row">
                      <span className="feed-name">{p.name || 'Someone new'}</span>
                      {p.age ? <span className="feed-age">, {p.age}</span> : null}
                      {p.hasVerifiedBadge && <VerifiedBadge size="small" />}
                    </div>
                    <div className={`feed-tag feed-tag--${meta.cls}`}>
                      <Icon aria-hidden="true" />
                      <span>{item.label}</span>
                    </div>
                    {mood ? (
                      <div className="feed-mood">{mood}</div>
                    ) : p.location ? (
                      <div className="feed-location">{p.location}</div>
                    ) : null}
                  </div>
                  <button
                    className="feed-sayhi"
                    onClick={(e) => sayHi(e, p.userId)}
                    aria-label={`Say hi to ${p.name || 'this member'}`}
                  >
                    <HiOutlineChat aria-hidden="true" />
                    <span>Say hi</span>
                  </button>
                </article>
              );
            })}
          </div>
        </>
      )}

      {/* Empty */}
      {!error && !hasContent && (
        <div className="feed-state">
          <div className="feed-state-icon"><HiOutlineSparkles /></div>
          <h3>It's quiet right now</h3>
          <p>
            {isFemale
              ? 'Share your first photo to get noticed, or browse and start a conversation.'
              : 'No posts or activity yet. Browse profiles and start a conversation.'}
          </p>
          <button className="feed-cta" onClick={() => navigate('/discover')}>Browse people</button>
        </div>
      )}
    </div>
  );
};

export default Feed;
